/**
 * O menu lateral — o lugar fixo que responde "onde estou e para onde vou".
 *
 * Antes de 12/08 não havia menu: cada tela montava o próprio cabeçalho de
 * destinos, e `/admin/turmas` e `/admin/escola` não tinham destino nenhum além
 * da seta de voltar. A única constante da interface era o avatar.
 *
 * Registro: **product** (DESIGN.md). O menu é mobília — ele fica ATRÁS da
 * tarefa, não disputa com ela:
 *   · nenhum adesivo, nenhuma sombra dura, nenhuma rotação;
 *   · item inativo NÃO recebe cor saturada (estratégia Restrained);
 *   · o único uso de cor é a barra do item ativo, que é indicador de estado;
 *   · `.font-marca` no nome da marca — NUNCA `.font-display`, que é a
 *     assinatura do 12brain e já vazou uma vez (Parte B2 do IDENTIDADE).
 *
 * Estado com dois sinais (princípio 3 do PRODUCT.md): o item ativo tem barra
 * DE COR e peso de fonte maior. Cor sozinha não passa em daltonismo.
 *
 * A montagem usa `ui/sidebar.tsx` (shadcn), que já estava no vocabulário do
 * projeto sem uso — DESIGN.md manda não inventar controle onde já existe um.
 * Ele traz de graça a gaveta no celular (via `ui/sheet`) e o atalho de teclado.
 */
import { Link, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { MenuDaConta } from '@/components/class/MenuDaConta';
import { useBranding } from '@/branding/BrandingProvider';
import { ARTE } from '@/branding/identidade';
import { FIXED_BRAND_ID } from '@/branding/product';
import { usePapel } from '@/hooks/usePapel';
import { menuDoPapel, itemAtivo } from '@/lib/navegacao';

export function MenuLateral() {
  const { pathname } = useLocation();
  const { papel, carregando } = usePapel();
  const { config } = useBranding();
  const arte = ARTE[FIXED_BRAND_ID ?? ''];
  const { isMobile, setOpenMobile } = useSidebar();

  /* Enquanto o papel não é conhecido não desenhamos os itens: mostrar o menu do
     aluno e trocar pelo do professor meio segundo depois é pior que esperar —
     e mostrar o do professor a quem não é seria o vazamento que o gate fecha. */
  const grupos = carregando ? [] : menuDoPapel(papel);

  /* Registro por papel (pedido do Matheus, 19/08): o menu do ALUNO é maior e
     com ícones na cor de cada item — é o celular do adolescente, registro
     lúdico. O do staff segue product: mobília sóbria, sem cor saturada. */
  const doAluno = papel === 'student';

  /* No celular a gaveta precisa fechar ao navegar. Sem isto o aluno toca em
     "Entrar na sala", a rota muda por baixo e ele continua olhando o menu. */
  const aoNavegar = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="offcanvas">
      {/* O TOPO É A MARCA. Com arte, o lockup entra no lugar do nome escrito —
          é o mesmo arquivo do login, então vem do cache e não custa download.

          Alinhado à ESQUERDA, na mesma margem dos itens: centralizar criaria uma
          segunda linha de leitura num menu que tem uma só.

          `max-w` e não altura fixa: o wordmark é ~5:1, e travar altura estoura a
          largura do menu. A 12rem ele fecha em ~44px de altura — conferido em
          tela que "NEXTGEN" continua legível nesse tamanho.

          Sem arte declarada, cai no nome na display da marca: é o que sustenta
          qualquer marca futura que ganhe menu sem ter logo no repo. */}
      <SidebarHeader className="px-4 py-4">
        {arte ? (
          <img
            src={arte.logo}
            alt={config.brandName}
            className="h-auto w-full max-w-[12rem]"
          />
        ) : (
          <span className="font-marca text-base font-bold leading-none tracking-tight">
            {config.brandName}
          </span>
        )}
        <span className="text-xs text-muted-foreground">Modo classe</span>
      </SidebarHeader>

      <SidebarContent>
        {grupos.map((grupo, i) => (
          <SidebarGroup key={grupo.titulo ?? `solto-${i}`}>
            {grupo.titulo && <SidebarGroupLabel>{grupo.titulo}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {grupo.itens.map((item) => {
                  const ativo = itemAtivo(pathname, item);
                  const Icone = item.icone;
                  return (
                    <SidebarMenuItem key={item.para}>
                      <SidebarMenuButton
                        asChild
                        isActive={ativo}
                        tooltip={item.descricao}
                        className={doAluno ? 'relative h-12 text-[15px]' : 'relative'}
                      >
                        <Link to={item.para} onClick={aoNavegar}>
                          {/* Barra de estado: o segundo sinal, junto do peso da
                              fonte. Fica sob o item, não desloca o texto. */}
                          {ativo && (
                            <span
                              aria-hidden="true"
                              className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full bg-primary"
                            />
                          )}
                          <Icone
                            aria-hidden="true"
                            className={doAluno ? '!size-5' : undefined}
                            style={doAluno && item.cor ? { color: item.cor } : undefined}
                          />
                          <span className={ativo ? 'font-semibold' : undefined}>
                            {item.rotulo}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* A conta mora no PÉ do menu, não flutuando no canto da tela: é o padrão
          que o usuário já procura, e tira o avatar de cima do conteúdo — onde
          ele comia o clique dos toasts (armadilha registrada no CLAUDE.md). */}
      <SidebarFooter className="border-t border-sidebar-border p-2">
        <MenuDaConta noPeDoMenu />
      </SidebarFooter>
    </Sidebar>
  );
}
